import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import type { MicrositeVideoData } from '~/model';

/* ── types ── */

interface MicrositeVideoProps {
	video: MicrositeVideoData;
	autostart?: boolean;
}

/* ── styled components ── */

const Video = styled.video`
	width: 100%;
	height: 100%;
`;

/* ── component ── */

const Player = styled.div`
	max-width: 64rem;
	margin: 0 auto;
	aspect-ratio: 16 / 9;
	background: black;
`;

export function MicrositeVideoPlayer(props: MicrositeVideoProps) {
	return (
		<Player>
			<MicrositeVideo {...props} />
		</Player>
	);
}

export default function MicrositeVideo({ video, autostart }: MicrositeVideoProps) {
	const videoRef = useRef<HTMLVideoElement>(null);

	useEffect(() => {
		const el = videoRef.current;
		if (!el) return;

		let hls: any;

		import('hls.js').then(({ default: Hls }) => {
			if (Hls.isSupported()) {
				if (autostart) {
					hls = new Hls({ startLevel: 2 });
					hls.loadSource(video.m3u8);
					hls.attachMedia(el);
				} else {
					hls = new Hls({ startLevel: -1, autoStartLoad: false });
					hls.loadSource(video.m3u8);
					hls.attachMedia(el);
					hls.startLoad(-1);
				}
			} else {
				el.src = video.mp4 ?? video.m3u8;
			}
		});

		return () => {
			hls?.destroy();
		};
	}, [video.m3u8, video.mp4]);

	return <Video ref={videoRef} playsInline controls crossOrigin="anonymous" poster={video.thumbnail.large_2x} />;
}
